import CloseIcon from "@mui/icons-material/Close";
import { Box, ButtonBase, IconButton, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { useState } from "react";

import { useContactCenterTheme } from "../virtual-receptionist/theming/ContactCenterThemeContext";
import { OpenChat } from "./chatDock.types";
import { useChatDock } from "./ChatDockContext";
import { buildChatDockLabel } from "./helpers/resolveChatLabel";
import { DOCK_HEADER_HEIGHT, DOCK_OVERFLOW_WIDTH } from "./useChatDockLayout";

export function ChatDockOverflowMenu({ chats }: { chats: OpenChat[] }) {
  const { theme } = useContactCenterTheme();
  const { focusChat, closeChat } = useChatDock();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const totalUnread = chats.reduce((total, chat) => total + chat.unreadCount, 0);

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={`${chats.length} more chats`}
        sx={{
          position: "relative",
          width: DOCK_OVERFLOW_WIDTH,
          minWidth: DOCK_OVERFLOW_WIDTH,
          height: DOCK_HEADER_HEIGHT,
          borderRadius: "12px 12px 0 0",
          backgroundColor: theme.shell.headerBg,
          border: `1px solid ${theme.shell.border}`,
          borderBottom: "none",
          color: theme.shell.textPrimary,
          fontSize: 14,
          fontWeight: 800,
          boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
        }}
      >
        {chats.length}

        {totalUnread > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: theme.shell.accent,
            }}
          />
        )}
      </ButtonBase>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: 2147483000 }}
        slotProps={{ paper: { sx: { minWidth: 220, backgroundColor: theme.shell.bg } } }}
      >
        {chats.map((chat) => (
          <MenuItem
            key={chat.key}
            onClick={() => {
              focusChat(chat.key);
              setAnchorEl(null);
            }}
            sx={{ color: theme.shell.textPrimary, gap: 1 }}
          >
            <ListItemText
              primary={
                <Typography noWrap sx={{ fontSize: 13, fontWeight: chat.unreadCount > 0 ? 800 : 500 }}>
                  {buildChatDockLabel(chat.companyName, chat.chatName)}
                </Typography>
              }
            />

            {chat.unreadCount > 0 && (
              <Box
                sx={{
                  minWidth: 16,
                  height: 16,
                  px: 0.5,
                  borderRadius: "999px",
                  backgroundColor: theme.shell.accent,
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 800,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
              </Box>
            )}

            <IconButton
              size="small"
              aria-label="Close chat"
              onClick={(event) => {
                event.stopPropagation();
                closeChat(chat.key);
              }}
              sx={{ width: 22, height: 22, color: theme.shell.textSecondary }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
